import { IBaseApiTypeModel, UserSessionContext, ViewModel, asApiError } from '@ViewModels';
import { action, makeObservable, observable, override } from 'mobx';
import { LightningType } from '../components/svgs/LightningGraphic';
import { IFile, IMeme, IMemeTheme, MemePointsPlacement } from './gamification';

export interface IActivityView extends IBaseApiTypeModel {
	points: number;
	label: string;
	id: string;
	/** Number of memes for this activity */
	memes: number;
}

export class ActivitiesViewModel extends ViewModel {
	public activities: ActivityViewModel[] = [];
	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			activities: observable,
			load: override,
			createMeme: action,
		});
	}

	public load = async () => {
		this.busy = true;
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IActivityView[]>(
			'meme/activities',
			'GET'
		);
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.activities = result.value.map(x => new ActivityViewModel(this.userSession, x));
		this.busy = false;
		this.loaded = true;
	};

	public createMeme = async (activityId: string, image: Blob) => {
		const newMeme: IMeme = {
			activities: [activityId],
			id: 'new',
			image: { mimeType: '', url: '' },
			memeType: LightningType.LightningDouble,
			phrase: '',
			theme: {
				backgroundColor: '#0061A6',
				pointsBackgroundColor: '#00AAE8',
				pointsColor: '#FFFFFF',
				pointsPlacement: MemePointsPlacement.TopRight,
				primaryColor: '#00F0C9',
				secondaryColor: '#0081DC',
			},
			value: 0,
		};

		// const placeholder = new File(null, 'placeholder.png');

		const body = new FormData();
		body.append('meme', JSON.stringify(newMeme));
		body.append('files', image);

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>('meme', 'POST', body);
		if (!result.success) {
			throw asApiError(result);
		}

		this.activities.find(x => x.id === activityId).memeVms.push(new MemeViewModel(this.userSession, result.value));
		this.loaded = true;
	};
}

export class ActivityViewModel extends ViewModel implements IActivityView {
	public points: number;
	public label: string;
	public id: string;
	public memes: number;
	public memeVms: MemeViewModel[] = [];

	constructor(userSession: UserSessionContext, view: IActivityView) {
		super(userSession);

		makeObservable(this, {
			points: observable,
			label: observable,
			id: observable,
			memes: observable,
			memeVms: observable,
			loadMemes: action,
		});

		this.points = view.points;
		this.label = view.label;
		this.id = view.id;
		this.memes = view.memes;
	}

	public loadMemes = async () => {
		this.busy = true;
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme[]>(
			`meme/byActivity/${this.id}`,
			'GET'
		);
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.memeVms = result.value.map(x => new MemeViewModel(this.userSession, x));
		this.busy = false;
		this.loaded = true;
	};
}

export class MemeViewModel extends ViewModel implements IMeme {
	public id: string;
	public activities: string[];
	public image: IFile;
	public value: number;
	public phrase: string;
	public memeType: LightningType;
	public theme: IMemeTheme;
	public sound: IFile;

	constructor(userSession: UserSessionContext, meme: IMeme) {
		super(userSession);

		makeObservable<MemeViewModel, 'set'>(this, {
			id: observable,
			activities: observable,
			image: observable,
			value: observable,
			phrase: observable,
			memeType: observable,
			theme: observable,
			sound: observable,
			set: action,
			load: override,
			updateImage: action,
			updateSound: action,
			updatePhrase: action,
			updateActivities: action,
			delete: action,
		});

		this.set(meme);
	}

	private set = (meme: IMeme) => {
		this.id = meme.id;
		this.activities = meme.activities;
		this.image = meme.image;

		this.value = meme.value;
		this.phrase = meme.phrase;
		this.memeType = meme.memeType;
		this.theme = meme.theme;

		this.sound = meme.sound;
	};

	public load = async () => {
		this.busy = true;
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(`meme/${this.id}`, 'GET');
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.set(result.value);

		this.busy = false;
		this.loaded = true;
	};

	public updateImage = async (file: Blob) => {
		this.busy = true;

		const body = new FormData();
		body.append('image', file);

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(
			`meme/${this.id}/image`,
			'PUT',
			body
		);
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.set(result.value);

		this.busy = false;
		this.loaded = true;
	};

	public updateSound = async (file: Blob) => {
		this.busy = true;

		const body = new FormData();
		body.append('sound', file);

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(
			`meme/${this.id}/sound`,
			'PUT',
			body
		);
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.set(result.value);

		this.busy = false;
		this.loaded = true;
	};

	public updatePhrase = async (phrase: string) => {
		this.busy = true;

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(
			encodeURI(`meme/${this.id}/phrase?phrase=${phrase}`),
			'PUT'
		);
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.set(result.value);

		this.busy = false;
		this.loaded = true;
	};

	public updateActivities = async (activities: string[]) => {
		this.busy = true;

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(`meme/${this.id}/phrase`, 'PUT', {
			activities,
		});
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.set(result.value);

		this.busy = false;
		this.loaded = true;
	};

	public delete = async () => {
		this.busy = true;

		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IMeme>(`meme/${this.id}`, 'DELETE');
		if (!result.success) {
			this.busy = false;
			throw asApiError(result);
		}

		this.busy = false;
		this.loaded = true;
	};
}
